import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { Folder, SharedDriveModel } from '../../../../../../types';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { StyledTreeItem } from '../styled-drive-tree-item';
import { handleErrors } from '../../../../../utils/errors';
import { Dispatch, SetStateAction } from 'react';
import { getUserPermission, UserKeys } from '../../../../../api/users-api';

import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import AddToDrive from '@mui/icons-material/AddToDrive';
import FolderIcon from '@mui/icons-material/Folder';
import FolderShared from '@mui/icons-material/FolderShared';

interface SharedDrivesFolderTreeProps {
  userEmail: string;
  selectedFolder: string;
  setSelectedFolder: React.Dispatch<React.SetStateAction<string>>;
  allFolders: InfiniteData<Folder[]> | undefined;
  sharedDrives: SharedDriveModel[];
  isLoading: boolean;
  disabledNodes?: string[];
  setOpenPermissionCheckDialog: Dispatch<SetStateAction<boolean>>;
  setSelectedDrive: Dispatch<SetStateAction<SharedDriveModel | null>>;
  permissionCheckLoading: boolean;
  setPermissionCheckLoading: Dispatch<SetStateAction<boolean>>;
}

export default function SharedDrivesFolderTree({
  userEmail,
  setSelectedFolder,
  selectedFolder,
  allFolders,
  isLoading,
  disabledNodes,
  sharedDrives,
  setPermissionCheckLoading,
  setOpenPermissionCheckDialog,
  setSelectedDrive,
}: SharedDrivesFolderTreeProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const allFoldersReduced = allFolders?.pages.reduce(
    (acc, page) => acc.concat(page),
    []
  );

  interface Node {
    id: string;
    title: string;
    iconUrl: string;
    parentId: string;
    children: Node[];
  }

  const folders =
    allFoldersReduced?.map((folder) => {
      return {
        id: folder.id,
        title: folder.title,
        iconUrl: 'folder',
        parentId: folder.parentId,
        children: [],
      };
    }) || [];

  const sharedDrivesFolders = sharedDrives.map((drive) => {
    return {
      id: drive.id,
      title: drive.name,
      iconUrl: 'drive',
      parentId: 'shared-drives',
      children: [],
    };
  });

  const uniqueFolders = folders
    ?.concat(sharedDrivesFolders)
    .filter(
      (value, index, self) => self.findIndex((v) => v.id === value.id) === index
    );

  // Function to convert the flat array into a tree structure
  function buildTree(folders: Node[], parentId: string) {
    const tree: Node[] = [];

    folders.forEach((folder) => {
      if (folder?.parentId === parentId) {
        const children = buildTree(folders, folder.id).sort((a, b) =>
          a.title.localeCompare(b.title)
        );

        if (children.length > 0) {
          folder.children = children;
        }
        tree.push(folder);
      }
    });

    return tree;
  }

  // Function to find the root node of a given node
  function findRootNode(nodeId: string): Node | null {
    let currentNode = uniqueFolders.find((node) => node.id === nodeId);
    while (
      currentNode &&
      currentNode.parentId !== 'shared-drives' &&
      currentNode.parentId
    ) {
      currentNode = uniqueFolders.find(
        (node) => node.id === currentNode?.parentId
      );
    }
    return currentNode || null;
  }

  const rootElements = buildTree(uniqueFolders || [], 'shared-drives').sort(
    (a, b) => a.title.localeCompare(b.title)
  );

  // recursive function to render folders
  const renderTree = (folders: Node[]) => {
    return folders.map((folder) => {
      return (
        <StyledTreeItem
          key={folder.id}
          itemId={folder.id}
          labelText={folder.title}
          labelIcon={
            folder.iconUrl.includes('drive')
              ? AddToDrive
              : folder.iconUrl.includes('shared')
              ? FolderShared
              : FolderIcon
          }
          labelInfo=""
          color={theme.palette.text.primary}
          bgColor={theme.palette.primary.light}
          disabled={disabledNodes?.includes(folder.id)}
        >
          {Array.isArray(folder.children) ? renderTree(folder.children) : null}
        </StyledTreeItem>
      );
    });
  };

  if (isLoading) {
    return (
      <Stack gap={1} sx={{ py: 0.5 }}>
        <Skeleton variant="rounded" width={'100%'} height={30} />
      </Stack>
    );
  } else {
    return (
      <Stack maxHeight={'100%'} sx={{ py: 0.5 }}>
        <SimpleTreeView
          aria-label="Shared Drive Folders"
          slots={{
            expandIcon: ArrowRightIcon,
            collapseIcon: ArrowDropDownIcon,
          }}
          defaultExpandedItems={['shared-drives']}
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            p: 0.25,
          }}
          onSelectedItemsChange={async (_event, nodeId) => {
            if (nodeId) {
              if (nodeId === 'shared-drives') {
                setSelectedFolder(nodeId);
                return;
              }

              try {
                setPermissionCheckLoading(true);

                const root = findRootNode(nodeId);

                const hasPermission = await queryClient.fetchQuery({
                  queryKey: [
                    UserKeys.SharedDriveUserPermission,
                    userEmail,
                    root?.id,
                  ],
                  queryFn: () =>
                    getUserPermission(root?.id || '', userEmail, true),
                  retry: false,
                  staleTime: 1000 * 60 * 2,
                });

                if (!hasPermission) {
                  const drive = sharedDrives.find(
                    (drive) => drive.id === nodeId
                  );

                  setSelectedDrive(drive!);
                  setOpenPermissionCheckDialog(true);
                } else {
                  setSelectedFolder(nodeId);
                }
              } catch (e) {
                handleErrors({
                  error: e,
                  snackbarMessage: `There was an issue checking your permissions. Please try again.`,
                });
              } finally {
                setPermissionCheckLoading(false);
              }
            }
          }}
          selectedItems={selectedFolder}
          multiSelect={false}
        >
          <StyledTreeItem
            itemId={`shared-drives`}
            labelText="Shared Drives"
            labelIcon={AddToDrive}
            color={theme.palette.text.primary}
            bgColor={theme.palette.primary.light}
          >
            {renderTree(rootElements)}
          </StyledTreeItem>
        </SimpleTreeView>
      </Stack>
    );
  }
}
